import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import { ThemeProvider } from "./components/ThemeProvider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { initSentry } from "./config/sentry.config";
import { initPostHog } from "./config/posthog.config";
import { initChunkErrorHandler } from "./utils/chunkErrorHandler";
import { uuidV4Fallback } from "./utils/uuid";
import { stashSlackOAuthRedirect } from "./helpers/slackOAuthRedirect.helpers";
import "@openuidev/react-ui/components.css";
import "@openuidev/react-ui/defaults.css";
import "./index.css";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

/**
 * `crypto.randomUUID` only exists in secure contexts (HTTPS / localhost). On a
 * plain-HTTP LAN origin (e.g. http://192.168.x.x:3001) it is undefined, and any
 * caller — including third-party chart code we don't own — throws a TypeError
 * that a route boundary would then disguise as an "Update available" card.
 * Install a guarded polyfill before React mounts so those bare calls succeed.
 * `crypto` can be a read-only accessor, so guard the assignment in try/catch.
 *
 * We install `uuidV4Fallback` — the raw generator — NOT the `randomUUID`
 * dispatcher. `randomUUID` decides at import time whether to delegate to the
 * native impl; installing it as `crypto.randomUUID` would make the value we
 * assign here the very thing a naive dispatcher re-reads, risking unbounded
 * recursion. The raw generator never touches `crypto.randomUUID`, so recursion
 * is structurally impossible regardless of how capture semantics evolve.
 */
function installRandomUUIDPolyfill(): void {
  const cryptoObj = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (!cryptoObj || typeof cryptoObj.randomUUID === "function") return;
  try {
    Object.defineProperty(cryptoObj, "randomUUID", {
      value: uuidV4Fallback,
      configurable: true,
      writable: true,
    });
  } catch (err) {
    console.warn("[uuid] could not install crypto.randomUUID polyfill:", err);
  }
}

installRandomUUIDPolyfill();
// Before any login redirect can drop the Slack OAuth-return query params.
stashSlackOAuthRedirect();
initChunkErrorHandler();
initSentry();
initPostHog();
const queryClient = new QueryClient();

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider defaultTheme="dark" enableAnimation={true}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>
);
